// oxlint-disable no-explicit-any
// oxlint-disable no-default-export
/* eslint-disable jsdoc/require-jsdoc, @typescript-eslint/naming-convention */

/*!
 * Hydroxie
 *
 * An unsophisticated bare-bones script that generates an HTMLElement in your browser,
 * and roughly syntax compatible with [Hyperscript](https://github.com/hyperhype/hyperscript).
 * It doesn't have any nifty features, but it just works without any dependencies.
 *
 * Usage:
 * * Copy this script into your project.
 * * Read [Hyperscript's doc](https://github.com/hyperhype/hyperscript?tab=readme-ov-file#example).
 *
 * @example
 * ```typescript
 * import h from "hydroxie";
 *
 * const elem = h("div", { className: "foo" },
 *     h("input", { type: "checkbox", value: "123" }));
 * ```
 *
 * Copyright (c) Kasugai Asuka
 * License: WTFPL
 */

import type { Except } from "type-fest";

type ChildElem = string | number | Node | null | undefined | ChildElem[];

type Props<TagName extends keyof HTMLElementTagNameMap> =
    Partial<Except<HTMLElementTagNameMap[TagName], "style"> & {
        data: Record<string, string>;
        style: Partial<CSSStyleDeclaration>;
    }>;

interface Hydroxie {
    /**
     * Create HTML Element.
     *
     * @template TagName
     * @param tagName Tag Name.
     * @param children Child elements.
     * @returns HTML Element.
     * @memberof Hydroxie
     */
    <TagName extends keyof HTMLElementTagNameMap>(tagName: TagName, ...children: ChildElem[]): HTMLElementTagNameMap[TagName];

    /**
     * Create HTML Element.
     *
     * @template TagName
     * @param tagName Tag Name.
     * @param props Properties.
     * @param children Child elements.
     * @returns HTML Element.
     * @memberof Hydroxie
     */
    <TagName extends keyof HTMLElementTagNameMap>(tagName: TagName, props: Props<TagName>, ...children: ChildElem[]): HTMLElementTagNameMap[TagName];

    /**
     * Create DocumentFragment.
     *
     * @template TagName
     * @param tagName Empty string.
     * @param children Child elements.
     * @returns DocumentFragment.
     * @memberof Hydroxie
     */
    (tagName: "", ...children: ChildElem[]): DocumentFragment;
}

const hx: Hydroxie = <TagName extends keyof HTMLElementTagNameMap>(tagName: TagName | "", propsOrFirstChild: Props<TagName> | ChildElem, ...children: ChildElem[]) => {
    if (tagName === "") {
        const elem = document.createDocumentFragment();
        const c = toFlatData([propsOrFirstChild, ...children].filter((x) => isChildElem(x)));
        elem.append(...c);
        return elem;
    } else {
        const elem = document.createElement(tagName);

        let p: Props<TagName> | undefined;
        let c;

        if (isChildElem(propsOrFirstChild)) {
            p = undefined;
            c = [propsOrFirstChild, ...children];
        } else {
            p = propsOrFirstChild;
            c = children;
        }

        if (p) {
            for (const [pkey, pvalue] of Object.entries(p)) {
                if (!Object.hasOwn(p, pkey)) {
                    continue;
                }

                if (pkey === "data") {
                    const d = p.data;
                    if (d) {
                        const dataset = elem.dataset;
                        for (const [dkey, dvalue] of Object.entries(d)) {
                            if (Object.hasOwn(d, dkey)) {
                                dataset[dkey] = dvalue;
                            }
                        }
                    }
                    continue;
                }

                if (pkey === "style") {
                    const s = p.style;
                    if (s) {
                        const style = elem.style;
                        for (const [skey, svalue] of Object.entries(s)) {
                            if (Object.hasOwn(s, skey)) {
                                (style as any)[skey] = svalue;
                            }
                        }
                    }
                    continue;
                }

                elem[pkey as keyof HTMLElementTagNameMap[TagName]] = pvalue as any;
            }
        }

        elem.append(...toFlatData(c));

        return elem;
    }
};

const toFlatData = (children: ChildElem[]) => {
    const result: Node[] = [];

    for (const c of children) {
        if (c == null) {
            continue;
        }

        if (Array.isArray(c)) {
            result.push(...toFlatData(c));
            continue;
        }

        if (typeof c === "number" || typeof c === "string") {
            const node = document.createTextNode("" + c);
            result.push(node);
            continue;
        }

        result.push(c);
    }

    return result;
};

const isChildElem = (item: unknown): item is ChildElem => {
    if (item == null) {
        return true;
    }

    if (typeof item === "string" || typeof item === "number") {
        return true;
    }

    return (item instanceof Node);
};

export default hx;
